import { useState, useEffect } from "react";
import logo from "../assets/images/ecom_tamil_logo.png";
import { useNavigate, useLocation } from "react-router-dom";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  
  const handleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (e, link) => {
    e.preventDefault();
    if (isHomePage) {
      const element = document.getElementById(link.id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, "", link.href);
      }
    } else {
      navigate(link.href);
    }
    if (isMenuOpen) closeMenu();
  };
  const handleLoginClick = () => {
    navigate("/login");
  };

  const handleLogoClick = () => {
    navigate("/");
    // Optional: Also scroll to top when navigating home
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', 
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const sections = ['hero', 'mission', 'services', 'tutorials', 'about', 'contact'];
    
    sections.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const navLinks = [
    { id: "hero", label: "Home", href: "/#hero" },
    { id: "mission", label: "Mission", href: "/#mission" },
    { id: "services", label: "Services", href: "/#services" },
    { id: "tutorials", label: "Tutorials", href: "/#tutorials" },
    { id: "about", label: "About Us", href: "/#about" },
    { id: "contact", label: "Contact Us", href: "/#contact" },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/80 backdrop-blur-md border-b border-amber-200/30 py-1' 
        : 'bg-transparent border-b border-transparent py-1'
    }`}>
      
      <div className="w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        
        {/* Logo with click handler */}
        <div className="flex items-center space-x-2 cursor-pointer" onClick={handleLogoClick}>
          <img 
            src={logo} 
            alt="Logo" 
            className="h-16 w-auto object-contain" 
          />
        </div>

        <div className="hidden md:flex items-center justify-end flex-1">
          <nav className="flex items-center space-x-8">
            {navLinks.map((link) => (
              <a 
                key={link.id}
                href={link.href} 
                onClick={(e) => handleNavClick(e, link)}
                className={`text-lg font-semibold transition-all duration-300 whitespace-nowrap relative py-1 group ${
                  isHomePage && activeSection === link.id 
                    ? 'text-amber-600' 
                    : 'text-gray-600 hover:text-amber-500'
                }`}
              >
                {link.label}
                <span className={`absolute bottom-0 left-0 h-0.5 bg-amber-600 transition-all duration-300 ${
                  isHomePage && activeSection === link.id ? 'w-full' : 'w-0 group-hover:w-full'
                }`}></span>
              </a>
            ))}
          </nav>
        </div>
        
        <div className="hidden md:flex items-center pl-10">
          <button 
            onClick={handleLoginClick}
            className="login-btn px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-lg transition-all duration-300 hover:scale-105"
          >
            Login
          </button>
        </div>
        
        <button 
          onClick={handleMenu} 
          className="md:hidden mr-2 p-2 text-gray-600 hover:text-amber-600 transition-colors duration-300"
        >
          {isMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      <div className={`md:hidden transition-all duration-300 ease-in-out ${
        isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
      }`}>
        <div className="px-4 py-3 space-y-2 bg-white border border-gray-100">
          {navLinks.map((link) => (
            <a 
              key={link.id}
              href={link.href} 
              onClick={(e) => handleNavClick(e, link)}
              className="block px-3 py-2 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </header>
  );
}